import { z } from "zod";

/**
 * Client-side mirror of the scheduling business rules (features/opening-hours-and-services.md). The
 * SERVER is authoritative (R2/R6/R7 are enforced there + at the DB CHECK); these helpers give the grid
 * editor immediate feedback — the cross-midnight confirm prompt (R2), the overlap highlight (R6) and
 * the service-form field validation (R7) — before the round-trip.
 */

export interface WindowDraft {
  dayOfWeek: number; // 1..7 (Mon..Sun)
  startTime: string; // "HH:mm"
  endTime: string; // "HH:mm"
  crossesMidnight: boolean;
  serviceId: string;
}

const MINUTES_PER_DAY = 24 * 60;
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;

function toMinutes(hhmm: string): number {
  const parts = hhmm.split(":");
  const h = Number.parseInt(parts[0] ?? "0", 10);
  const m = Number.parseInt(parts[1] ?? "0", 10);
  return h * 60 + m;
}

/** R2: end not after start on the wall clock — this shape REQUIRES the crossesMidnight flag. */
export function endNotAfterStart(startTime: string, endTime: string): boolean {
  return toMinutes(endTime) <= toMinutes(startTime);
}

/** The half-open `[from, to)` minute-of-week interval a window occupies (cross-midnight wraps past 7d). */
function interval(w: WindowDraft): { from: number; to: number } {
  const start = toMinutes(w.startTime);
  const end = toMinutes(w.endTime);
  const from = (w.dayOfWeek - 1) * MINUTES_PER_DAY + start;
  const duration = w.crossesMidnight ? MINUTES_PER_DAY - start + end : end - start;
  return { from, to: from + duration };
}

function occupiedMinutes(w: WindowDraft): Set<number> {
  const { from, to } = interval(w);
  const out = new Set<number>();
  for (let i = from; i < to; i++)
    out.add(((i % MINUTES_PER_WEEK) + MINUTES_PER_WEEK) % MINUTES_PER_WEEK);
  return out;
}

/** R6: half-open circular overlap between two SAME-service windows. Touching endpoints do not overlap. */
export function windowsOverlap(a: WindowDraft, b: WindowDraft): boolean {
  if (a.serviceId !== b.serviceId) return false;
  const bMinutes = occupiedMinutes(b);
  for (const m of occupiedMinutes(a)) if (bMinutes.has(m)) return true;
  return false;
}

/**
 * The zero-based indices of every window caught in a same-service overlap (R6). Windows whose
 * `endNotAfterStart` and are NOT flagged crossesMidnight are skipped (their interval is ill-defined —
 * they are flagged by [endNotAfterStart] instead). Drives the grid's overlap highlight.
 */
export function findOverlaps(windows: WindowDraft[]): Set<number> {
  const flagged = new Set<number>();
  const valid = windows
    .map((w, i) => ({ w, i }))
    .filter(({ w }) => !(endNotAfterStart(w.startTime, w.endTime) && !w.crossesMidnight));
  for (let a = 0; a < valid.length; a++) {
    for (let b = a + 1; b < valid.length; b++) {
      const first = valid[a];
      const second = valid[b];
      if (first && second && windowsOverlap(first.w, second.w)) {
        flagged.add(first.i);
        flagged.add(second.i);
      }
    }
  }
  return flagged;
}

/** The MVP-allowed per-service slot granularities (minutes), constrained {15,30,60} by CHECK. */
export const ALLOWED_GRANULARITY = [15, 30, 60] as const;

/**
 * The service-form zod schema (ADR-0027). Mirrors R7/SC-2 (buffer ≥ 0, cap > 0) and the party range;
 * `t` supplies localized messages. The server remains authoritative — a 422 lands field errors back.
 */
export function serviceFormSchema(t: (key: string) => string) {
  return z
    .object({
      nameFr: z.string().min(1, t("schedule.error.nameRequired")),
      partyMin: z.coerce.number().int().min(1, t("schedule.error.partyMin")),
      partyMax: z.coerce.number().int().min(1, t("schedule.error.partyMin")),
      onlineBookable: z.boolean(),
      turnoverBufferMinutes: z.coerce.number().int().min(0, t("schedule.error.buffer")),
      coversCapPerSlot: z.coerce
        .number()
        .int()
        .positive(t("schedule.error.cap"))
        .optional()
        .or(z.literal("").transform(() => undefined)),
      slotGranularityMinutes: z.coerce.number().int().optional(),
    })
    .refine((v) => v.partyMax >= v.partyMin, {
      message: t("schedule.error.partyRange"),
      path: ["partyMax"],
    });
}

export type ServiceFormValues = z.infer<ReturnType<typeof serviceFormSchema>>;
